// Utilitários para exportação de dados
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Função para converter dados para CSV
export const exportToCSV = (data, filename = 'dados', headers = null) => {
  try {
    console.log('📄 Exportando para CSV:', { data: data?.length, filename });
    
    if (!data || data.length === 0) {
      throw new Error('Nenhum dado disponível para exportação');
    }

    // Verificar se há problemas [object Object] nos dados
    const hasObjectIssues = data.some(item => 
      Object.values(item).some(value => 
        typeof value === 'object' && value !== null && !Array.isArray(value)
      )
    );
    
    if (hasObjectIssues) {
      console.warn('⚠️ Detectados objetos aninhados que podem causar [object Object]. Corrigindo...');
      
      // Corrigir objetos aninhados
      data = data.map(item => {
        const corrected = {};
        Object.keys(item).forEach(key => {
          const value = item[key];
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            // Tentar extrair valor útil
            if (value.hasOwnProperty('name')) corrected[key] = value.name;
            else if (value.hasOwnProperty('title')) corrected[key] = value.title;
            else if (value.hasOwnProperty('label')) corrected[key] = value.label;
            else if (value.hasOwnProperty('value')) corrected[key] = value.value;
            else if (Object.keys(value).length === 1) corrected[key] = Object.values(value)[0];
            else corrected[key] = JSON.stringify(value);
          } else {
            corrected[key] = value;
          }
        });
        return corrected;
      });
    }

    // Determinar headers automaticamente ou usar os fornecidos
    const csvHeaders = headers || Object.keys(data[0]);
    
    // Função para escapar valores CSV
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '';
      
      let stringValue = String(value);
      
      // Verificar se ainda há [object Object]
      if (stringValue === '[object Object]') {
        console.error('❌ Ainda há [object Object] nos dados:', value);
        stringValue = '[Erro: Objeto não serializado]';
      }
      
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    // Criar header CSV
    const csvHeader = csvHeaders.join(',');
    
    // Criar linhas CSV
    const csvRows = data.map(item => 
      csvHeaders.map(header => escapeCSV(item[header])).join(',')
    );
    
    // Combinar header e dados
    const csvContent = [csvHeader, ...csvRows].join('\n');
    
    // Verificar se há [object Object] no conteúdo final
    if (csvContent.includes('[object Object]')) {
      console.error('❌ [object Object] encontrado no CSV final!');
      console.log('🔍 Dados problemáticos:', data.slice(0, 3));
    }
    
    // Criar e baixar arquivo
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadFile(blob, `${filename}.csv`);
    
    console.log('✅ CSV exportado com sucesso');
    return true;
  } catch (error) {
    console.error('❌ Erro ao exportar CSV:', error);
    throw error;
  }
};

// Função para exportar para Excel (usando HTML table)
export const exportToExcel = (data, filename = 'dados', headers = null, title = '') => {
  try {
    console.log('📊 Exportando para Excel:', { data: data?.length, filename });
    
    if (!data || data.length === 0) {
      throw new Error('Nenhum dado disponível para exportação');
    }

    const excelHeaders = headers || Object.keys(data[0]);
    
    // Criar HTML da tabela Excel
    let htmlContent = `
      <html>
        <head>
          <meta charset="utf-8">
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .title { font-size: 18px; font-weight: bold; margin-bottom: 20px; }
            .export-info { font-size: 12px; color: #666; margin-bottom: 10px; }
          </style>
        </head>
        <body>
    `;
    
    if (title) {
      htmlContent += `<div class="title">${title}</div>`;
    }
    
    htmlContent += `
      <div class="export-info">
        Exportado em: ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: ptBR })} | 
        Total de registros: ${data.length}
      </div>
      <table>
        <thead>
          <tr>
    `;
    
    // Headers
    excelHeaders.forEach(header => {
      htmlContent += `<th>${header}</th>`;
    });
    
    htmlContent += '</tr></thead><tbody>';
    
    // Dados
    data.forEach(item => {
      htmlContent += '<tr>';
      excelHeaders.forEach(header => {
        const value = item[header] || '';
        htmlContent += `<td>${String(value).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>`;
      });
      htmlContent += '</tr>';
    });
    
    htmlContent += '</tbody></table></body></html>';
    
    // Criar e baixar arquivo
    const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel' });
    downloadFile(blob, `${filename}.xls`);
    
    console.log('✅ Excel exportado com sucesso');
    return true;
  } catch (error) {
    console.error('❌ Erro ao exportar Excel:', error);
    throw error;
  }
};

// Função para exportar para PDF (usando HTML e print)
export const exportToPDF = (data, filename = 'dados', headers = null, title = '') => {
  try {
    console.log('📑 Exportando para PDF:', { data: data?.length, filename });
    
    if (!data || data.length === 0) {
      throw new Error('Nenhum dado disponível para exportação');
    }

    const pdfHeaders = headers || Object.keys(data[0]);
    
    // Criar nova janela para impressão
    const printWindow = window.open('', '_blank');
    
    let htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>${title || filename}</title>
          <style>
            @page { 
              size: A4 landscape; 
              margin: 1cm; 
            }
            body { 
              font-family: Arial, sans-serif; 
              font-size: 10px; 
              margin: 0; 
              padding: 0; 
            }
            .header { 
              text-align: center; 
              margin-bottom: 20px; 
              border-bottom: 2px solid #333; 
              padding-bottom: 10px; 
            }
            .title { 
              font-size: 18px; 
              font-weight: bold; 
              margin-bottom: 5px; 
            }
            .export-info { 
              font-size: 12px; 
              color: #666; 
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-top: 20px; 
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 6px; 
              text-align: left; 
              word-wrap: break-word; 
            }
            th { 
              background-color: #f5f5f5; 
              font-weight: bold; 
              font-size: 9px; 
            }
            td { 
              font-size: 8px; 
            }
            .footer { 
              position: fixed; 
              bottom: 1cm; 
              left: 1cm; 
              right: 1cm; 
              text-align: center; 
              font-size: 8px; 
              color: #666; 
              border-top: 1px solid #ccc; 
              padding-top: 5px; 
            }
            @media print {
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">${title || 'Relatório de Dados'}</div>
            <div class="export-info">
              Exportado em: ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: ptBR })} | 
              Total de registros: ${data.length}
            </div>
          </div>
          
          <div class="no-print" style="margin-bottom: 20px; text-align: center;">
            <button onclick="window.print();" style="padding: 10px 20px; font-size: 14px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
              🖨️ Imprimir / Salvar como PDF
            </button>
            <button onclick="window.close();" style="padding: 10px 20px; font-size: 14px; background: #6c757d; color: white; border: none; border-radius: 4px; cursor: pointer; margin-left: 10px;">
              ❌ Fechar
            </button>
          </div>
          
          <table>
            <thead>
              <tr>
    `;
    
    // Headers
    pdfHeaders.forEach(header => {
      htmlContent += `<th>${header}</th>`;
    });
    
    htmlContent += '</tr></thead><tbody>';
    
    // Dados (limitar para não quebrar o PDF)
    const limitedData = data.slice(0, 1000); // Máximo 1000 registros por PDF
    limitedData.forEach(item => {
      htmlContent += '<tr>';
      pdfHeaders.forEach(header => {
        let value = item[header] || '';
        // Limitar tamanho do texto para não quebrar layout
        if (String(value).length > 50) {
          value = String(value).substring(0, 47) + '...';
        }
        htmlContent += `<td>${String(value).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>`;
      });
      htmlContent += '</tr>';
    });
    
    htmlContent += `
            </tbody>
          </table>
          
          <div class="footer">
            ${title || 'Relatório'} - Página 1 de 1 | Gerado pelo HelpDeskPro em ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Auto print após carregar
    printWindow.onload = () => {
      printWindow.focus();
    };
    
    console.log('✅ PDF preparado para impressão');
    return true;
  } catch (error) {
    console.error('❌ Erro ao exportar PDF:', error);
    throw error;
  }
};

// Função para baixar arquivo
const downloadFile = (blob, filename) => {
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Limpar URL objeto após um tempo
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
};

// Função principal de exportação com múltiplos formatos
export const exportData = async (data, options = {}) => {
  const {
    format: exportFormat = 'csv',
    filename = 'dados_exportados',
    headers = null,
    title = '',
    customHeaders = {}
  } = options;

  try {
    console.log('📤 Iniciando exportação:', { format: exportFormat, filename, records: data?.length });

    if (!data || data.length === 0) {
      throw new Error('Nenhum dado disponível para exportação');
    }

    // Aplicar headers customizados se fornecidos
    let processedData = data;
    if (customHeaders && Object.keys(customHeaders).length > 0) {
      processedData = data.map(item => {
        const newItem = {};
        Object.keys(item).forEach(key => {
          const newKey = customHeaders[key] || key;
          newItem[newKey] = item[key];
        });
        return newItem;
      });
    }

    // Adicionar timestamp ao filename
    const timestamp = format(new Date(), 'yyyyMMdd_HHmm');
    const finalFilename = `${filename}_${timestamp}`;

    // Executar exportação baseada no formato
    switch (exportFormat.toLowerCase()) {
      case 'csv':
        return await exportToCSV(processedData, finalFilename, headers);
      
      case 'excel':
      case 'xls':
        return await exportToExcel(processedData, finalFilename, headers, title);
      
      case 'pdf':
        return await exportToPDF(processedData, finalFilename, headers, title);
      
      default:
        throw new Error(`Formato de exportação não suportado: ${exportFormat}`);
    }
  } catch (error) {
    console.error('❌ Erro na exportação:', error);
    throw error;
  }
};

// Utilitários para formatação de dados específicos
export const formatDataForExport = {
  // Formatar dados de tickets
  tickets: (tickets) => {
    return tickets.map(ticket => ({
      'Número': ticket.ticket_number || ticket.id,
      'Título': ticket.title,
      'Status': ticket.status,
      'Prioridade': ticket.priority,
      'Cliente': ticket.client_name || ticket.company_name || 'N/A',
      'Responsável': ticket.assigned_user_name || 'Não atribuído',
      'Criado em': ticket.created_at ? format(new Date(ticket.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 'N/A',
      'Vencimento': ticket.due_date ? format(new Date(ticket.due_date), 'dd/MM/yyyy', { locale: ptBR }) : 'N/A',
      'Resolvido em': ticket.resolved_at ? format(new Date(ticket.resolved_at), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 'Não resolvido'
    }));
  },

  // Formatar dados de usuários
  users: (users) => {
    return users.map(user => ({
      'Nome': user.name || user.full_name,
      'Email': user.email,
      'Função': user.role,
      'Empresa': user.company_name || 'N/A',
      'Admin': user.is_admin ? 'Sim' : 'Não',
      'Criado em': user.created_at ? format(new Date(user.created_at), 'dd/MM/yyyy', { locale: ptBR }) : 'N/A',
      'Status': user.active ? 'Ativo' : 'Inativo'
    }));
  },

  // Formatar dados de empresas
  companies: (companies) => {
    return companies.map(company => ({
      'Nome': company.name || company.corporate_name,
      'Email': company.email,
      'Telefone': company.phone || 'N/A',
      'Endereço': company.address || 'N/A',
      'Website': company.website || 'N/A',
      'Status': company.is_active ? 'Ativa' : 'Inativa',
      'Criada em': company.created_at ? format(new Date(company.created_at), 'dd/MM/yyyy', { locale: ptBR }) : 'N/A'
    }));
  },

  // Formatar dados de projetos
  projects: (projects) => {
    return projects.map(project => ({
      'Nome': project.name,
      'Descrição': project.description || 'N/A',
      'Status': project.status,
      'Prioridade': project.priority,
      'Cliente': project.client_name || 'N/A',
      'Gerente': project.manager_name || 'N/A',
      'Início': project.start_date ? format(new Date(project.start_date), 'dd/MM/yyyy', { locale: ptBR }) : 'N/A',
      'Fim Previsto': project.end_date ? format(new Date(project.end_date), 'dd/MM/yyyy', { locale: ptBR }) : 'N/A',
      'Progresso': `${project.progress || 0}%`,
      'Criado em': project.created_at ? format(new Date(project.created_at), 'dd/MM/yyyy', { locale: ptBR }) : 'N/A'
    }));
  },

  // Formatar estatísticas genéricas
  stats: (stats) => {
    // Se é um array de objetos já formatados, retornar como está
    if (Array.isArray(stats) && stats.length > 0 && typeof stats[0] === 'object') {
      return stats.map(item => {
        const formatted = {};
        Object.keys(item).forEach(key => {
          const value = item[key];
          if (typeof value === 'number') {
            formatted[key] = value.toLocaleString('pt-BR');
          } else if (typeof value === 'object' && value !== null) {
            // Tentar extrair valor útil do objeto
            if (value.name) formatted[key] = value.name;
            else if (value.title) formatted[key] = value.title;
            else if (value.label) formatted[key] = value.label;
            else formatted[key] = JSON.stringify(value);
          } else {
            formatted[key] = String(value || '');
          }
        });
        return formatted;
      });
    }
    
    // Se é um objeto simples de chave-valor
    if (typeof stats === 'object' && !Array.isArray(stats)) {
      return Object.entries(stats).map(([key, value]) => ({
        'Métrica': key,
        'Valor': typeof value === 'number' ? value.toLocaleString('pt-BR') : String(value)
      }));
    }
    
    // Fallback
    return [{ 'Erro': 'Formato de dados não reconhecido' }];
  }
};

export default {
  exportData,
  exportToCSV,
  exportToExcel,
  exportToPDF,
  formatDataForExport
}; 